import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Tipos de datos
export interface Dependency {
  id: string;
  name: string;
  code: string;
  responsible: string;
  email: string;
  phone: string;
  process_count: number;
  active_tasks_count: number;
  rating: number;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  dependency_id: string;
  phone: string;
  status: string;
  last_login: string;
  created_at: string;
  updated_at: string;
}

export interface Process {
  id: string;
  name: string;
  code: string;
  type: string;
  dependency_id: string;
  responsible: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  progress: number;
  tasks_total: number;
  tasks_completed: number;
  tasks_pending: number;
  tasks_overdue: number;
  created_at: string;
  updated_at: string;
}

export interface Task {
  id: string;
  name: string;
  process_id: string;
  dependency_id: string;
  responsible: string;
  assigned_to: string;
  priority: string;
  status: string;
  start_date: string;
  due_date: string;
  completed_date: string | null;
  progress: number;
  description: string;
  evidence_count: number;
  approved_by: string | null;
  approved_at: string | null;
  approval_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Evidence {
  id: string;
  task_id: string;
  file_name: string;
  file_type: string;
  file_size: string;
  file_url: string | null;
  uploaded_by: string;
  upload_date: string;
  description: string;
  created_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  task_id: string;
  task_name: string;
  type: 'aprobada' | 'devuelta' | 'asignada';
  message: string;
  read: boolean;
  created_at: string;
}

export interface TaskActivityLog {
  id: string;
  task_id: string;
  changed_by: string;
  previous_status: string | null;
  new_status: string;
  notes: string | null;
  created_at: string;
}

export interface ProcessChangeLog {
  id: string;
  process_id: string;
  changed_by: string;
  changed_at: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  change_type: string;
  notes: string | null;
  created_at: string;
}

export interface TaskChangeLog {
  id: string;
  task_id: string;
  changed_by: string;
  changed_at: string;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  change_type: string;
  notes: string | null;
}

export interface ControlEvaluation {
  id: string;
  process_id: string | null;
  process_name: string | null;
  evaluator_id: string | null;
  evaluator_name: string | null;
  evaluation_date: string;
  quality_score: number | null;
  normative_compliance: boolean | null;
  normative_observation: string | null;
  documentation_complete: boolean | null;
  identified_risks: string | null;
  general_observations: string | null;
  overall_status: 'Borrador' | 'En revisión' | 'Aprobada' | 'Archivada';
  created_at: string;
  updated_at: string;
}

export interface ControlFinding {
  id: string;
  evaluation_id: string;
  finding_type: 'Crítico' | 'Moderado' | 'Leve';
  description: string;
  evidence_url: string | null;
  evidence_name: string | null;
  responsible: string | null;
  finding_date: string | null;
  created_at: string;
}

export interface ControlImprovementPlan {
  id: string;
  evaluation_id: string;
  corrective_action: string;
  responsible: string;
  deadline: string | null;
  status: 'Pendiente' | 'En proceso' | 'Completado' | 'Vencido';
  created_at: string;
  updated_at: string;
}